import { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { Apollo } from 'src/gql/generated';

export function useGetUnassignedWorkflowPermissions({
  skip = false,
  workflowId,
}: {
  workflowId: UUID;
  skip?: boolean;
}) {
  const { data, loading } = Apollo.useGetWorkflowAssignmentsQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    skip,
    variables: { workflowId },
  });

  return {
    loading,
    unassignedWorkflowPermissions:
      data?.workflow?.permissions
        .filter(p => p.roleName && !p.account)
        .sort((a, b) => {
          return a.roleName && b.roleName ? a.roleName.localeCompare(b.roleName) : 0;
        }) ?? [],
  };
}

export function useGetUnassignedWorkflowPermissionsLazy(
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetWorkflowAssignmentsLazyQuery>[0]>,
    'variables'
  >
) {
  const [getUnassignedWorkflowPermissionsQuery] =
    Apollo.useGetWorkflowAssignmentsLazyQuery(options);
  const getUnassignedWorkflowPermissions = useCallback(
    async (callOptions: { variables: Apollo.GetWorkflowAssignmentsQueryVariables }) => {
      const { data } = await getUnassignedWorkflowPermissionsQuery(callOptions);

      return (
        data?.workflow?.permissions
          ?.filter(p => p?.roleName && !p?.account)
          .sort((a, b) => {
            return a?.roleName && b?.roleName ? a.roleName.localeCompare(b.roleName) : 0;
          }) ?? []
      );
    },
    [getUnassignedWorkflowPermissionsQuery]
  );

  return {
    getUnassignedWorkflowPermissions,
  };
}
